// First-steps tutorial: automatic villages make a tool and plan their camp;
// manual villages learn their first tool order. Pure
// client overlay: each step advances off REAL game state (selection, the
// hall's forge queue), never off "next" buttons, so it teaches the actual
// motions. Runs once PER VILLAGE (the done-flag is keyed to the faction id),
// so founding or restarting a village starts the tour over; ✕ dismisses it
// for that village. Owner: client-ui.

import type { Building } from '../../shared/types'
import { townHallPhase } from '../../shared/townhall'
import { hammerCraftStatus, openingStage } from './opening'
import { suggestOpeningPlot } from './openingPlot'
import { el, type UICtx } from './panels'
import { MOBILE_HUD_QUERY } from './mobile'

/** Done-flag storage key for one village — a new faction id starts fresh. */
const doneKey = (factionId: string): string => `hexTutorial1:${factionId}`

export function initTutorial(ctx: UICtx): void {
  const { st, root, jumpTo } = ctx

  const card = el('div', 'tut-card hidden')
  const text = el('div', 'tut-text')
  text.setAttribute('aria-live', 'polite')
  const skip = el('button', 'tut-skip', '✕') as HTMLButtonElement
  skip.title = 'Skip the tour'
  skip.setAttribute('aria-label', 'Dismiss tutorial')
  card.appendChild(text)
  card.appendChild(skip)
  const progress=el('div','tut-progress hidden')
  const crafting=el('div','tut-crafting hidden')
  const craftStatus=el('div','tut-craft-status')
  craftStatus.setAttribute('role','status')
  const craftMeter=el('progress','tut-craft-meter') as HTMLProgressElement
  craftMeter.max=1
  craftMeter.setAttribute('aria-label','Hammer crafting progress')
  crafting.append(craftStatus,craftMeter)
  card.prepend(progress)
  card.append(crafting)
  const showCrafting=(hall:Building):void=>{
    const status=hammerCraftStatus(hall,st.units.values(),st.self?.stockpile,st.connected,st.self?.realm,st.tick)
    crafting.classList.toggle('hidden',!status)
    if(status){
      if(craftStatus.textContent!==status.label)craftStatus.textContent=status.label
      craftMeter.value=status.fraction
      craftMeter.setAttribute('aria-valuetext',status.label)
    }
  }
  root.appendChild(card)

  // A pointer and a target glow guide the next click without obscuring the HUD.
  const arrow = el('div', 'tut-arrow hidden')
  const bob = el('span', 'tut-bob', '▼')
  arrow.appendChild(bob)
  root.appendChild(arrow)

  let step: 'wait' | 'select' | 'craft' | 'done' | 'off' = 'wait'
  // The village this session's tour already served — founding or restarting a
  // village swaps st.me to a NEW faction id, which re-arms the tour. (The old
  // code tore the whole overlay down on finish, so a restart in the same
  // session never saw a tutorial again.)
  let ranFor: string | null = null
  const hideAll = (): void => {
    card.classList.add('hidden')
    arrow.classList.add('hidden')
    root.classList.remove('opening-tour','opening-craft')
    for (const b of document.querySelectorAll('.tut-glow')) b.classList.remove('tut-glow')
  }
  const finish = (): void => {
    step = 'off'
    ranFor = st.me ?? ranFor
    hideAll()
    try {
      if (st.me) localStorage.setItem(doneKey(st.me), '1')
    } catch {
      // storage unavailable — the session keeps its dismissal via `ranFor`
    }
  }
  skip.onclick = finish

  let autoTarget: string | null = null // 'hall' anchors to the world; other targets are selectors
  let autoPlacement: 'farm' | 'house' | null = null
  let suggestedPlot: { q: number; r: number } | null = null
  st.on('tutorialRestart', () => {
    if (!st.me) return
    try { localStorage.removeItem(doneKey(st.me)) } catch { /* session-only replay */ }
    step='wait';ranFor=null;autoTarget=null;autoPlacement=null;suggestedPlot=null
    hideAll()
  })
  const guide = (html: string, target: string | null, placement: 'farm' | 'house' | null = null): void => {
    if (target !== autoTarget && target?.startsWith('[data-build-')) st.emit('ui:build:reveal')
    autoTarget = target
    autoPlacement = placement
    show(html)
  }

  const hallOf = (): Building | undefined => {
    for (const b of st.buildings.values()) if (b.owner === st.me && b.type === 'townhall' && b.done) return b
    return undefined
  }

  const show = (html: string): void => {
    if (text.innerHTML !== html) text.innerHTML = html
    card.classList.remove('hidden')
  }

  // The guide never performs a game action. Teach the same world selection,
  // category tabs and controls that remain available after dismissal.
  const guideBench=(hall:Building,resume=false):void=>{
    if(st.buildMode){
      guide('Press <b>Esc</b> to leave building placement, then click your <b>Town Hall campsite</b> on the map to open its panel.','hall')
    }else if(st.selection.building!==hall.id){
      guide(resume
        ? 'Your bench is paused. Click your <b>Town Hall campsite</b> on the map to open its panel.'
        : '<b>Two people. A place to begin.</b> Click your <b>Town Hall campsite</b> on the map — the tent and campfire — to access its crafting bench.','hall')
    }else if(resume){
      guide('Click <b>Resume work</b> in the campsite panel to continue making your hammer.','[data-building-action="pause"]')
    }else{
      const craftingOpen=root.querySelector('[data-building-tab="craft"]')?.getAttribute('aria-selected')==='true'
      if(craftingOpen)guide('In <b>Crafting</b>, click <b>Hammer</b> (10 wood). Return to this campsite panel whenever you need another tool.','[data-craft-item="hammer"]')
      else guide('Click the <b>Crafting</b> tab in the campsite panel to see the tool recipes.','[data-building-tab="craft"]')
    }
  }
  const guidePlacement=(hall:Building,kind:'farm'|'house',waiting=false):void=>{
    guide(`Plan your ${kind} <b>near the campsite</b>, below the <b>gold arrow</b>, or choose another green preview. Trees are fine — your crew clears them first.${waiting?' Your builders will start once the hammer is ready.':''}`,null,kind)
    const visiblePlot=(p:{q:number;r:number}):boolean=>{
      const screen=st.projectHex?.(p.q,p.r)
      if(!screen?.vis||document.elementFromPoint(screen.x,screen.y)?.id!=='game')return false
      // Pointer-transparent guidance and tooltips can still hide the ground.
      for(const overlay of root.querySelectorAll<HTMLElement>('.tut-card, .tip, .restip')){
        const style=getComputedStyle(overlay)
        if(style.display==='none'||style.visibility==='hidden'||Number(style.opacity)===0)continue
        const r=overlay.getBoundingClientRect()
        if(screen.x>r.left-18&&screen.x<r.right+18&&screen.y>r.top-20&&screen.y<r.bottom+20)return false
      }
      return true
    }
    // Reconsider after rotation, new plans, terrain changes or camera movement.
    suggestedPlot=suggestOpeningPlot(st,hall,kind,visiblePlot)
    if(!suggestedPlot){
      show('Keep your first buildings <b>close to the campsite</b>. Pan around it or close a panel to see nearby ground, and rotate the preview with <b>R</b> if needed. Trees can be cleared; choose a green plot your crew can walk to.')
    }
  }
  const guideBuild=(hall:Building,kind:'farm'|'house',waiting=false):void=>{
    if(st.buildMode===kind){guidePlacement(hall,kind,waiting);return}
    const group=kind==='farm'?'gather':'town'
    const label=kind==='farm'?'Gathering':'Town'
    const name=kind==='farm'?'Farm':'House'
    const lead=waiting?'<b>Keep playing while your hammer is made.</b> ':''
    const button=`[data-build-type="${kind}"]`
    if(!root.querySelector(button)){
      guide(`${lead}Click <b>${label}</b> in the build menu at the bottom to find ${kind==='farm'?'food buildings':'housing'}.`,`[data-build-group="${group}"]`)
    }else{
      guide(`${lead}Click <b>${name}</b> in the <b>${label}</b> menu, then choose its spot on the map. ${kind==='farm'?'Farms feed your people.':'Houses make room for more villagers.'}`,button)
    }
  }

  let tourFor: string | null = null // faction the running tour is serving
  setInterval(() => {
    if (step === 'off') {
      // A new village founded THIS session (restart or first founding after a
      // dismissed tour) gets its own fresh tour.
      if (!st.me || st.me === ranFor) return
      step = 'wait'
    } else if (step !== 'wait' && st.me && tourFor && st.me !== tourFor) {
      // Restarted mid-tour: the new village starts its own tour from the top.
      step = 'wait'
      hideAll()
    }
    const hall = hallOf()
    if (step === 'wait') {
      // Arm once THIS village exists — and skip silently if this village
      // already ran (or dismissed) its tour. A different faction id (a new
      // or restarted village) starts over.
      if (!hall || !st.me) return
      try {
        if (localStorage.getItem(doneKey(st.me)) === '1') {
          step = 'off'
          ranFor = st.me
          hideAll()
          return
        }
      } catch {
        // storage unreadable — run the tour; worst case it repeats
      }
      step = 'select'
      autoTarget = null; autoPlacement = null; suggestedPlot = null
      tourFor = st.me
      if(st.self?.governor?.enabled!==false){
        jumpTo(hall.q,hall.r)
        st.cam.zoom=1.5
        st.emit('openingFocus')
      }
      skip.textContent = '✕'
      skip.classList.remove('tut-done-btn')
    }
    if (!hall) { hideAll(); return }
    // Panels remain fully usable; guidance returns when the player closes them.
    if(root.querySelector('.modal-back:not(.hidden),.journal-back:not(.hidden),.adventure-back:not(.hidden)')){
      hideAll();return
    }
    crafting.classList.add('hidden')
    skip.setAttribute('aria-label','Dismiss tutorial')
    if(st.self?.governor?.enabled!==false){
      root.classList.add('opening-tour')
      card.classList.add('tut-opening')
      progress.classList.remove('hidden')
      const hammerReady=!!st.self?.realm?.firstHammerMade||(st.self?.items.hammer??0)>0||[...st.units.values()].some(u=>u.owner===st.me&&u.person?.held==='hammer')
      const stage=townHallPhase(hall.hallPhase)>=2?'ready':openingStage(st.me!,st.buildings.values(),hammerReady,hall.queue.some(o=>o.item==='hammer'))
      const number=stage==='hammer'||stage==='crafting'?1:stage==='farm'?2:3
      progress.textContent=stage==='ready'?'✓ YOUR VILLAGE IS UNDERWAY':`${number} / 3  ·  ${stage==='hammer'?'MAKE YOUR FIRST TOOL':stage==='crafting'?'CRAFTING · KEEP PLAYING':stage==='farm'?'FEED YOUR PEOPLE':'MAKE ROOM TO GROW'}`
      progress.setAttribute('aria-label',stage==='ready'?'Opening complete':`Opening step ${number} of 3`)
      skip.textContent='✕';skip.classList.remove('tut-done-btn')
      if(autoPlacement&&st.buildMode!==autoPlacement)suggestedPlot=null
      if(stage==='hammer'){
        guideBench(hall)
      }else if(stage==='crafting'){
        showCrafting(hall)
        const own=[...st.buildings.values()].filter(b=>b.owner===st.me)
        const next=!own.some(b=>b.type==='farm'||b.type==='pier')?'farm':!own.some(b=>b.type==='house')?'house':null
        if(next)guideBuild(hall,next,true)
        else guide('<b>Your first village is planned.</b> Select people to give orders, or use the build menu. To check crafting later, click your campsite and open the <b>Crafting</b> tab. Your crew will start construction when the hammer is ready.',null)
        if(hall.paused)guideBench(hall,true)
      }else if(stage==='ready'){
        guide(townHallPhase(hall.hallPhase)>=2
          ? '🏕️ <b>Your Settler Camp is established.</b> Open the <b>Village Council</b> to check food, manage arrivals and organize your <b>Village Watch</b> when spare villagers are ready.'
          : '🏡 <b>Your first buildings are planned.</b> While people finish the work, open the <b>Village Council</b> to check food and choose <b>Build a Settler Camp</b>. That project guides your village toward the 500-wood upgrade.','.journal-launch')
        skip.textContent='Done';skip.classList.add('tut-done-btn')
        skip.setAttribute('aria-label','Finish tutorial')
      }else if(stage==='farm'||stage==='house'){
        guideBuild(hall,stage)
      }
      return
    }
    root.classList.remove('opening-tour','opening-craft');card.classList.add('tut-opening')
    progress.classList.add('hidden')
    if(step==='done'&&!hall.queue.some(o=>o.item==='hammer')&&!st.self?.realm?.firstHammerMade&&!(st.self?.items.hammer??0)&&![...st.units.values()].some(u=>u.owner===st.me&&u.person?.held==='hammer')){
      step='craft';skip.textContent='✕';skip.classList.remove('tut-done-btn')
    }
    if (step === 'select') {
      guideBench(hall)
      if (st.selection.building === hall.id) step = 'craft'
    }
    if (step === 'craft') {
      guideBench(hall)
      const queued = hall.queue.some((o) => o.item === 'hammer')
      const racked = (st.self?.items?.hammer ?? 0) > 0
      if (queued || racked) {
        step = 'done'
        for (const b of document.querySelectorAll('.tut-glow')) b.classList.remove('tut-glow')
      }
    }
    if (step === 'done') {
      showCrafting(hall)
      guide('✅ <b>Keep playing!</b> Use <b>Gathering → Farm</b> or <b>Town → House</b> in the build menu while crafting continues. When the hammer is ready, equip it on a founder to start building.',null)
      if(hall.paused&&hall.queue.some(o=>o.item==='hammer'))guideBench(hall,true)
      skip.textContent = 'Done'
      skip.classList.add('tut-done-btn')
      skip.setAttribute('aria-label','Finish tutorial')
    }
    // 150ms: the panel rebuilds its DOM every tick and wipes the glow — a
    // slower re-apply reads as flicker on the very button we're pointing at.
  }, 150)

  // Re-measure pointers as the camera and selection panel move, without
  // scrolling panels, changing selection or taking over placement mode.
  let lastSpotTarget: Element | null = null
  const hide = (): void => {
    arrow.classList.add('hidden')
    lastSpotTarget?.classList.remove('tut-glow')
    lastSpotTarget=null
  }
  /** World-anchored pointer to the hall. */
  const spotHall = (): void => {
    hide()
    const hall = hallOf()
    const p = hall && st.projectHex ? st.projectHex(hall.q, hall.r) : null
    if (p && p.vis) {
      arrow.classList.remove('hidden')
      bob.textContent = '▼'
      bob.classList.remove('tut-bob-x')
      arrow.style.left = `${p.x}px`
      arrow.style.top = `${p.y - 74}px`
      arrow.style.transform = 'translateX(-50%)'
    } else if (p) {
      // The arrow clamps to the viewport edge and points toward the hall.
      arrow.classList.remove('hidden')
      bob.textContent = '▼'
      bob.classList.remove('tut-bob-x')
      const x = Math.max(40, Math.min(window.innerWidth - 40, p.x))
      const y = Math.max(80, Math.min(window.innerHeight - 140, p.y))
      const deg = (Math.atan2(p.y - y, p.x - x) * 180) / Math.PI - 90
      arrow.style.left = `${x}px`
      arrow.style.top = `${y}px`
      arrow.style.transform = `translateX(-50%) rotate(${deg}deg)`
    } else hide()
  }
  /** Highlight the recommended control; all surrounding controls stay usable. */
  const spotEl = (target: Element): void => {
    const r = target.getBoundingClientRect()
    if(!r.width||!r.height||r.bottom<0||r.top>innerHeight){hide();return}
    const hit=document.elementFromPoint(r.left+r.width/2,r.top+r.height/2)
    if(!hit||!target.contains(hit)){hide();return}
    if(lastSpotTarget!==target)lastSpotTarget?.classList.remove('tut-glow')
    lastSpotTarget=target
    target.classList.add('tut-glow')
    const cy = r.top + r.height / 2
    arrow.classList.remove('hidden')
    bob.textContent = '◀'
    bob.classList.add('tut-bob-x')
    arrow.style.left = `${Math.round(Math.min(innerWidth-32,r.right + 12))}px`
    arrow.style.top = `${Math.round(cy)}px`
    arrow.style.transform = 'translateY(-55%)'
  }
  const spotlight = (): void => {
    // Follow the actual dock bounds, including responsive layout and its
    // build-mode scale animation, so guidance never covers menu controls.
    if (!card.classList.contains('hidden')) {
      const dock = root.querySelector('.buildbar')?.getBoundingClientRect()
      const compact = window.matchMedia(MOBILE_HUD_QUERY).matches
      let floor=compact&&dock?.height?dock.bottom:window.innerHeight
      const hint=root.querySelector('.hintbar:not(.hidden)')?.getBoundingClientRect()
      const bounds=card.getBoundingClientRect()
      if(dock?.height&&dock.left<bounds.right&&dock.right>bounds.left)floor=Math.min(floor,dock.top)
      if(hint?.height&&hint.top>innerHeight/2&&hint.left<bounds.right&&hint.right>bounds.left)floor=Math.min(floor,hint.top)
      if(compact){
        const selected=root.querySelector('.sel-panel:not(.hidden)')?.getBoundingClientRect()
        if(selected?.height&&selected.left<bounds.right&&selected.right>bounds.left)floor=Math.min(floor,selected.top)
      }
      const bottom = `${Math.ceil(window.innerHeight-floor+12)}px`
      if (card.style.getPropertyValue('--tutorial-bottom') !== bottom) {
        card.style.setProperty('--tutorial-bottom', bottom)
      }
    }
    if(card.classList.contains('hidden')){hide();requestAnimationFrame(spotlight);return}
    if(step==='off'||step==='wait')hide()
    else if(autoTarget==='hall')spotHall()
    else if(autoTarget){
      const target=root.querySelector(autoTarget)
      if(target)spotEl(target)
      else hide()
    }else if(autoPlacement&&suggestedPlot){
      hide()
      const p=st.projectHex?.(suggestedPlot.q,suggestedPlot.r)
      if(p?.vis){
        arrow.classList.remove('hidden');bob.textContent='▼';bob.classList.remove('tut-bob-x')
        arrow.style.left=`${p.x}px`;arrow.style.top=`${p.y-60}px`;arrow.style.transform='translateX(-50%)'
      }else hide()
    }else hide()
    requestAnimationFrame(spotlight)
  }
  requestAnimationFrame(spotlight)
}
